import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import MediaCard from "@/components/feature/MediaCard";
import FilterBar from "@/pages/category/components/FilterBar";
import {
  applyFilters,
  getHotMedia,
  searchMedia,
  type FilterState,
} from "@/lib/media";

const container = "mx-auto w-full max-w-[1280px] px-4 md:px-6";
const defaultFilters: FilterState = { region: "全部", year: "全部", minRating: 0 };

const categoryTabs = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
];

export default function Search() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get("query") ?? "";

  const [input, setInput] = useState(query);
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [categoryTab, setCategoryTab] = useState("all");

  useEffect(() => {
    setInput(query);
    setFilters(defaultFilters);
    setCategoryTab("all");
  }, [query]);

  useEffect(() => {
    document.title = query
      ? `搜索“${query}” - 红狐影视`
      : "搜索影视作品 - 红狐影视";
  }, [query]);

  const results = useMemo(() => {
    let list = searchMedia(query);
    if (categoryTab !== "all") {
      list = list.filter((m) => m.category === categoryTab);
    }
    list = applyFilters(list, filters);
    return list.sort((a, b) => b.recommendWeight - a.recommendWeight);
  }, [query, categoryTab, filters]);

  const hot = getHotMedia(6);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    if (!q) return;
    navigate(`/search?query=${encodeURIComponent(q)}`);
  };

  return (
    <div className={`${container} py-8 md:py-10`}>
      <nav aria-label="面包屑导航" className="flex items-center gap-1.5 text-sm">
        <Link to="/" className="text-foreground-500 hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line text-foreground-300"></i>
        <span className="font-medium text-foreground-800">搜索结果</span>
      </nav>

      <header className="mt-5">
        <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          搜索影视作品
        </h1>
        {query ? (
          <p className="mt-2 text-sm text-foreground-600">
            关于 <strong className="text-primary-600">“{query}”</strong> 的搜索结果，共{" "}
            <strong className="text-primary-600">{results.length}</strong> 条匹配内容
          </p>
        ) : (
          <p className="mt-2 text-sm text-foreground-600">
            输入关键词，快速查找电影、电视剧、导演或演员
          </p>
        )}
      </header>

      <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-foreground-400"></i>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="输入影视名称、导演或演员，如：星际、悬疑…"
            className="w-full rounded-md border border-background-200 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            aria-label="搜索影视作品"
          />
        </div>
        <button
          type="submit"
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
        >
          <i className="ri-search-line"></i>
          搜索
        </button>
      </form>

      {query && (
        <>
          <div className="mt-5 rounded-lg border border-background-200/70 bg-background-50 p-4">
            <div className="flex flex-wrap gap-2">
              {categoryTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setCategoryTab(tab.key)}
                  className={`whitespace-nowrap rounded-md px-4 py-1.5 text-sm transition-colors cursor-pointer ${
                    categoryTab === tab.key
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-700"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <FilterBar
              filters={filters}
              onChange={setFilters}
              onReset={() => setFilters(defaultFilters)}
            />
          </div>
        </>
      )}

      {results.length > 0 ? (
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
          {results.map((item, i) => (
            <MediaCard key={item.id} item={item} index={i} />
          ))}
        </div>
      ) : (
        <div className="mt-8">
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <i className="ri-search-eye-line text-5xl text-foreground-300"></i>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">
              {query
                ? "未找到相关影视内容"
                : "输入关键词开始搜索"}
            </h2>
            <p className="mt-2 max-w-md text-sm text-foreground-500">
              {query
                ? "换个关键词试试，或者放宽筛选条件，也可以看看下面这些热门推荐。"
                : "试试搜索“科幻”“悬疑”“爱情”等类型关键词。"}
            </p>
          </div>

          <section className="mt-10">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-fire-fill text-primary-500"></i>
              热门影视推荐
            </h2>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
              {hot.map((item, i) => (
                <MediaCard key={item.id} item={item} index={i} />
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}